import { computed, ref, watch } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import { useTimeSlotPager } from '~/composables/useTimeSlotPager'
import type { CalendarDay } from '~/interfaces/CalendarDay'
import type { EventDateTime } from '~/interfaces/EventDateTime'
import type { EventDateTimeSelectionSource } from '~/interfaces/EventDateTimeSelectionSource'
import { buildTimeOptions } from '~/utils/buildTimeOptions'
import { convertTo12Hour } from '~/utils/convertTo12Hour'
import { convertTo24Hour } from '~/utils/convertTo24Hour'
import { getEffectiveTimezone } from '~/utils/getEffectiveTimezone'
import { isOrderTimeSlotAvailable } from '~/utils/isOrderTimeSlotAvailable'
import type { TimeSlotStore } from '~/utils/TimeSlotStore'

// A full day of 15-minute slots. Store rules filter this list by date.
const ALL_TIME_SLOTS = buildTimeOptions(15, convertTo12Hour)

interface UseEventDateTimeSelectionReturn {
  apply: () => void
  canApplySelection: ComputedRef<boolean>
  canGoEarlier: ComputedRef<boolean>
  canGoLater: ComputedRef<boolean>
  clear: () => void
  effectiveTimezone: ComputedRef<string>
  goEarlier: () => void
  goLater: () => void
  hasNoAvailableTimes: ComputedRef<boolean>
  selectDate: (day: CalendarDay) => void
  selectedCalendarDate: Ref<string | null>
  selectedTime: Ref<string>
  selectTime: (time: string) => void
  timeSlots: ComputedRef<string[]>
  validationError: ComputedRef<string>
}

/** The date and time a person is choosing, limited to the slots the store and menus allow on that date. */
export const useEventDateTimeSelection = (
  source: EventDateTimeSelectionSource,
  onApply: (value: EventDateTime) => void
): UseEventDateTimeSelectionReturn => {
  const t = usePresentationTranslator()
  const selectedCalendarDate = ref<string | null>(null)
  const selectedTime = ref('')
  const validationErrorKey = ref('')
  const validationError = computed(() =>
    validationErrorKey.value ? t(validationErrorKey.value) : ''
  )

  const effectiveTimezone = computed(() => getEffectiveTimezone(source.timezone))
  const storeWithTimezone = computed<TimeSlotStore | null>(() =>
    source.store === null ? null : { ...source.store, timezone: effectiveTimezone.value }
  )

  const isSlotAvailable = (time24: string, eventDate: string) =>
    source.menus.isValid &&
    isOrderTimeSlotAvailable(time24, {
      eventDate,
      menus: source.menus.menus,
      orderType: source.orderType,
      store: storeWithTimezone.value
    })

  const availableTimeSlots = computed(() => {
    const eventDate = selectedCalendarDate.value
    const open =
      eventDate === null
        ? ALL_TIME_SLOTS
        : ALL_TIME_SLOTS.filter((slot) => isSlotAvailable(slot.value, eventDate))
    return open.map((slot) => slot.label)
  })

  const pager = useTimeSlotPager(availableTimeSlots)

  const hasNoAvailableTimes = computed(
    () => Boolean(selectedCalendarDate.value) && availableTimeSlots.value.length === 0
  )

  const canApplySelection = computed(() => {
    const eventDate = selectedCalendarDate.value
    if (hasNoAvailableTimes.value) return false
    if (eventDate === null || selectedTime.value.length === 0) return true

    return isSlotAvailable(convertTo24Hour(selectedTime.value), eventDate)
  })

  const selectDate = (day: CalendarDay) => {
    selectedCalendarDate.value = day.date
    pager.resetToFirstPage()
    validationErrorKey.value = ''

    if (selectedTime.value && !isSlotAvailable(convertTo24Hour(selectedTime.value), day.date)) {
      selectedTime.value = ''
      validationErrorKey.value = availableTimeSlots.value.length
        ? 'commerce.dateTimeFilter.selectAnAvailableEventTime'
        : 'commerce.dateTimeFilter.noTimesAreAvailableForThisDate'
    }
  }

  const selectTime = (time: string) => {
    selectedTime.value = time
    validationErrorKey.value = ''
  }

  const apply = () => {
    const eventTime = convertTo24Hour(selectedTime.value)
    const eventDate = selectedCalendarDate.value

    if (hasNoAvailableTimes.value) {
      validationErrorKey.value = 'commerce.dateTimeFilter.noTimesAreAvailableForThisDate'
      return
    }

    if (eventDate !== null && eventTime && !isSlotAvailable(eventTime, eventDate)) {
      validationErrorKey.value = 'commerce.dateTimeFilter.selectAnAvailableEventTime'
      return
    }

    // Both values go out in one event so the search runs once.
    onApply({ eventDate, eventTime })
  }

  const clear = () => {
    selectedCalendarDate.value = null
    selectedTime.value = ''
    validationErrorKey.value = ''
    onApply({ eventDate: null, eventTime: null })
  }

  const initializeFromSource = () => {
    validationErrorKey.value = ''
    const eventDate = source.currentEventDate
    const eventTime = source.currentEventTime

    selectedCalendarDate.value = eventDate === null || eventDate === '' ? null : eventDate
    selectedTime.value = eventTime === null || eventTime === '' ? '' : convertTo12Hour(eventTime)
    pager.resetToFirstPage()

    const selectedIndex = availableTimeSlots.value.findIndex((slot) => slot === selectedTime.value)
    if (selectedTime.value !== '' && selectedIndex !== -1) pager.centerOnSlotAt(selectedIndex)

    if (
      selectedCalendarDate.value !== null &&
      eventTime !== null &&
      eventTime !== '' &&
      !isSlotAvailable(eventTime, selectedCalendarDate.value)
    ) {
      validationErrorKey.value = 'commerce.dateTimeFilter.selectedTimeNoLongerAvailable'
    }
  }

  watch(
    () => [
      source.currentEventDate,
      source.currentEventTime,
      source.orderType,
      source.store,
      source.menus
    ],
    initializeFromSource,
    { immediate: true }
  )

  return {
    apply,
    canApplySelection,
    canGoEarlier: pager.canGoEarlier,
    canGoLater: pager.canGoLater,
    clear,
    effectiveTimezone,
    goEarlier: pager.goEarlier,
    goLater: pager.goLater,
    hasNoAvailableTimes,
    selectDate,
    selectedCalendarDate,
    selectedTime,
    selectTime,
    timeSlots: pager.pageSlots,
    validationError
  }
}
